import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import api from "../../../services/api";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import SupplyItemFormModal from "./SupplyItemFormModal";
import { useAppConfig } from "../../../context/AppConfigContext";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const SupplyItemTable = () => {
  const [items, setItems] = useState([]);
  const [filteredDept, setFilteredDept] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { config } = useAppConfig();

  const snackbar = useSnackbarAlert();

  const fetchItems = async () => {
    try {
      const res = await api.get("/admin/supply-items");
      setItems(res.data);
    } catch (err) {
      console.error("Failed to load supply items", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filteredItems = items.filter((item) => {
    if (!item) return false;

    const matchesActive = onlyActive ? item.isActive : true;
    const matchesDept = filteredDept ? item.department === filteredDept : true;

    return matchesActive && matchesDept;
  });

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/supply-items/${deleteTarget._id}`);
      snackbar.success("Item deleted!");
      setDeleteTarget(null);
      await fetchItems();
    } catch (err) {
      console.error("Failed to delete item", err);
    }
  };

  const handleSave = async () => {
    setEditingItem(null);
    setModalOpen(false);
    await fetchItems();
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}>
        <Typography variant="h5">Supply Items</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingItem(null);
            setModalOpen(true);
          }}>
          Add Item
        </Button>
      </Box>

      <Stack direction="row" spacing={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="dept-filter-label" shrink>
            Department
          </InputLabel>
          <Select
            labelId="dept-filter-label"
            label="Department"
            value={filteredDept}
            onChange={(e) => setFilteredDept(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return "All";
              return selected;
            }}>
            <MenuItem value="">All</MenuItem>
            {config?.supplyDepartments?.map((dept) => (
              <MenuItem key={dept} value={dept}>
                {dept}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Active</InputLabel>
          <Select
            value={onlyActive ? "active" : "all"}
            label="Active"
            onChange={(e) => setOnlyActive(e.target.value === "active")}>
            <MenuItem value="all">Show All</MenuItem>
            <MenuItem value="active">Only Active</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Measuremnt Unit</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Path Index</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => (
              <TableRow key={item._id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.department || "—"}</TableCell>
                <TableCell>
                  <Chip
                    key={item.measurementUnit}
                    label={item.measurementUnit}
                    size="small"
                    sx={{ mr: 0.5 }}
                  />
                </TableCell>
                <TableCell>{item.supplier?.name || "—"}</TableCell>
                <TableCell>{item.storePathIndex ?? "—"}</TableCell>
                <TableCell>
                  {item.isActive ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="Inactive" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingItem(item);
                      setModalOpen(true);
                    }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(item)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modals */}
      <SupplyItemFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialData={editingItem}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Supply Item"
        description={`Are you sure you want to delete "${deleteTarget?.name}"?`}
      />
    </Box>
  );
};

export default SupplyItemTable;
